import {signedDownloadCache} from './SignedDownloadCache';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {fileMetadata, putSignedImage, SignedDownload, SignedUpload} from './SignedImageUpload';
import {baseAPI} from './BaseAPI';
import {TAGS} from './TagTypes';
import {PaginatedResponse} from '../models/Admin';
import {AdminFeedbackDetails, AdminFeedbackListItem, FeedbackCreated, FeedbackFilters, FeedbackStatus} from '../models/Feedback';

const cleanParams = (values: FeedbackFilters) => Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== ''),
);

const screenshotUploads = new WeakMap<File, Map<string, SignedUpload>>();

export const feedbackAPI = baseAPI.injectEndpoints({
    endpoints: build => ({
        sendFeedback: build.mutation<FeedbackCreated, {type: string; message: string; relatedTraceId?: string; currentPage?: string; appVersion?: string; technicalContext?: string}>({
            query: body => ({url: 'feedback', method: 'POST', body}),
        }),
        uploadFeedbackScreenshot: build.mutation<null, {id: string; file: File}>({
            async queryFn({id, file}, _api, _options, query) {
                try {
                    let sessions = screenshotUploads.get(file);
                    if (!sessions) { sessions = new Map(); screenshotUploads.set(file, sessions); }
                    let upload = sessions.get(id);
                    if (!upload || Date.parse(upload.expiresAt) <= Date.now()) {
                        const ticket = await query({url: 'feedback/' + encodeURIComponent(id) + '/screenshot/upload-url', method: 'POST', body: fileMetadata(file, 5242880)});
                        if (ticket.error) return {error: ticket.error};
                        upload = ticket.data as SignedUpload;
                        sessions.set(id, upload);
                    }
                    await putSignedImage(upload, file);
                    const complete = await query({url: 'feedback/' + encodeURIComponent(id) + '/screenshot/complete', method: 'POST', body: {uploadToken: upload.uploadToken}});
                    if (complete.error && (complete.error.data as {code?: string} | undefined)?.code === 'image.upload.invalid') sessions.delete(id);
                    return complete.error ? {error: complete.error} : {data: null};
                } catch (error) { return {error: {status: 'CUSTOM_ERROR', error: String(error)}}; }
            },
        }),
        getAdminFeedback: build.query<PaginatedResponse<AdminFeedbackListItem>, FeedbackFilters>({
            query: filters => ({url: 'admin/feedback', params: cleanParams(filters)}),
            providesTags: result => [TAGS.ADMIN_FEEDBACK, ...(result?.data.map(item => ({type: TAGS.ADMIN_FEEDBACK, id: item.id})) ?? [])],
        }),
        getAdminFeedbackDetails: build.query<AdminFeedbackDetails, string>({
            query: id => `admin/feedback/${encodeURIComponent(id)}`,
            providesTags: (_result, _error, id) => [{type: TAGS.ADMIN_FEEDBACK, id}],
        }),
        getAdminFeedbackScreenshot: build.query<SignedDownload, string>({
            async queryFn(id, _api, _options, query) {
                try {
                    const data = await signedDownloadCache.get('feedback:' + id, async () => {
                        const result = await query({url: 'admin/feedback/' + encodeURIComponent(id) + '/screenshot'});
                        if (result.error) throw result.error;
                        return result.data as SignedDownload;
                    });
                    return {data};
                } catch (error) { return {error: error as FetchBaseQueryError}; }
            },
        }),
        updateAdminFeedbackStatus: build.mutation<void, {id: string; status: FeedbackStatus}>({
            query: ({id, status}) => ({
                url: `admin/feedback/${encodeURIComponent(id)}/status`,
                method: 'PATCH',
                body: {status},
            }),
            invalidatesTags: (_result, _error, {id}) => [TAGS.ADMIN_FEEDBACK, {type: TAGS.ADMIN_FEEDBACK, id}],
        }),
    }),
});
